import { DateInput, theme } from '@arnold/common';
import styled from '@emotion/styled/macro';
import intersection from 'lodash.intersection';
import { forwardRef } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import LoadingLine from '../../components/LoadingLine';
import { ReportSequenceSubscriptionSubscription, TopicGroupTypeCode } from '../../generated/hooks';
import { Filter } from '../../lib/reportHelper';
import { AggregatedData } from '../../lib/reports/aggregateData';
import { usePushReportEvent } from '../ReportGTMContext';
import { FilterWrapper, FiltersScrollContainer } from './GlobalFilter/GlobalFilter';
import GlobalGenericFilter from './GlobalGenericFilter';
import GlobalTeamFilter from './GlobalTeamFilter';
import { FiltersContainer, PredefinedFilter } from './StyledComponents';

const SequenceTeamFilterContainer = styled.div`
  min-width: 200px;
  width: calc((100% - 2rem) / 3);
  max-width: 300px;
  flex-grow: 1;

  @media (max-width: ${theme.breakpoint.small}) {
    width: 100%;
    max-width: 100%;
  }
`;

const CustomLoadingLine = styled(LoadingLine)`
  position: relative;
  top: 2px;
  width: 150px;
`;

const StyledDateInput = styled(DateInput)`
  min-width: 200px;
  width: calc((100% - 2rem) / 3);
  max-width: 300px;
  flex-grow: 1;

  @media (max-width: ${theme.breakpoint.small}) {
    width: 100%;
    max-width: 100%;
  }
`;

const InfoContainer = styled.div`
  display: flex;
  justify-content: end;
  gap: ${theme.spacing.f};
  margin-top: ${theme.spacing.f};
  margin-bottom: ${theme.spacing.d};
  align-items: center;

  @media (max-width: ${theme.breakpoint.small}) {
    border-top: 1px solid ${theme.colors.borderMain.default};
    margin-top: 0;
    margin-bottom: ${theme.spacing.c};
    margin-left: -${theme.spacing.f};
    margin-right: -${theme.spacing.f};
    padding-left: ${theme.spacing.f};
    padding-right: ${theme.spacing.f};
    padding-top: 24px;
  }
`;

interface IProps {
  sequenceStart: Date;
  sequenceEnd: Date;
  allRespondentsCount: number;
  filteredRespondentsCount: number;
  rootTeamsIds: string[];
  respondentId?: string | null;
  aggregatedData: AggregatedData | null;
  setTeamsFilter: (teams: string[]) => void;
  teamsFilter: string[];
  addedOnFrom: string | null | undefined;
  addedOnTo: string | null | undefined;
  onAddedOnFromChange: (newValue: string | null) => void;
  onAddedOnToChange: (newValue: string | null) => void;
  setAddedOnAndLoad: (from: string | null, to: string | null) => void;
  typeCode?: TopicGroupTypeCode | null;
  onConfirm: () => void;
  loading: boolean;
  classNames?: string;
  hiringInfo?: NonNullable<NonNullable<ReportSequenceSubscriptionSubscription>['getProcessReport']>['hiringInfo'];
  filters: Filter[];
  setFilters: (filters: Filter[]) => void;
  organizationId: string;
}

const ResultsInfo = styled.div`
  text-align: right;
  color: ${theme.colors.text.secondary};
  margin-top: ${theme.spacing.c};
`;

const PredefinedFiltersWrapper = styled.div`
  margin-bottom: ${theme.spacing.e};
`;

const GlobalSequenceFilter = forwardRef<HTMLDivElement, IProps>(
  (
    {
      onAddedOnFromChange,
      onAddedOnToChange,
      sequenceStart,
      sequenceEnd,
      allRespondentsCount,
      filteredRespondentsCount,
      rootTeamsIds,
      respondentId,
      aggregatedData,
      setTeamsFilter,
      teamsFilter,
      addedOnFrom,
      addedOnTo,
      setAddedOnAndLoad,
      typeCode,
      loading,
      onConfirm,
      classNames,
      hiringInfo,
      filters,
      setFilters,
      organizationId,
    },
    ref,
  ) => {
    const { t } = useTranslation('Report');
    const pushReportEvent = usePushReportEvent();

    const pushDateFilterEvent = (filterItem: string, filterValue?: string) => {
      const hasExistingValues = !!(addedOnFrom || addedOnTo);
      const eventName = hasExistingValues ? 'filter_updated' : 'filter_applied';
      pushReportEvent(eventName, eventName, {
        filter_category: 'date',
        filter_item: filterItem,
        ...(filterValue && { filter_value: filterValue }),
      });
    };

    const getCandidateIds = (operatorType: string, fieldName: 'positions' | 'recruiters' | 'managers') => {
      const values = filters
        .filter((f) => f.operator === operatorType)
        .map((f) => f.values)
        .flat();
      const candidateIds = hiringInfo?.[fieldName]
        ?.filter((p) => values.includes(p.id))
        .map((p) => p.candidateIds)
        .flat();
      return candidateIds?.length ? candidateIds : null;
    };

    const positionCandidateIds = getCandidateIds('position', 'positions');
    const recruiterCandidateIds = getCandidateIds('recruiter', 'recruiters');
    const managerCandidateIds = getCandidateIds('manager', 'managers');

    return (
      <FilterWrapper
        ref={ref}
        className={`bg-white rounded-lg shadow-sm px-8 py-6 ${classNames}`}
        data-cy="report-sequence-filter"
        data-icom={
          typeCode === TopicGroupTypeCode.Onboarding
            ? 'onboarding-report-filters'
            : typeCode === TopicGroupTypeCode.Exit
              ? 'exit-report-filters'
              : 'process-report-filters'
        }
      >
        <FiltersScrollContainer>
          <PredefinedFiltersWrapper>
            <PredefinedFilter
              onClick={() => {
                const date = new Date();
                const to = date.toISOString().split('T')[0];
                date.setDate(date.getDate() - 30);
                const from = date.toISOString().split('T')[0];
                pushDateFilterEvent('last_30_days');
                setAddedOnAndLoad(from, to);
              }}
              data-icom={'seq-filter-last30Days'}
            >
              {t('ReportSequence:lastDays', { count: 30 })}
            </PredefinedFilter>
            <PredefinedFilter
              onClick={() => {
                const date = new Date();
                const to = date.toISOString().split('T')[0];
                date.setDate(date.getDate() - 90);
                const from = date.toISOString().split('T')[0];
                pushDateFilterEvent('last_90_days');
                setAddedOnAndLoad(from, to);
              }}
              data-icom={'seq-filter-last90Days'}
            >
              {t('ReportSequence:lastDays', { count: 90 })}
            </PredefinedFilter>
            <PredefinedFilter
              onClick={() => {
                const date = new Date();
                date.setDate(1);
                date.setDate(date.getDate() - 1);
                const to = date.toISOString().split('T')[0];
                date.setDate(1);
                const from = date.toISOString().split('T')[0];
                pushDateFilterEvent('last_month');
                setAddedOnAndLoad(from, to);
              }}
              data-icom={'seq-filter-lastMonth'}
            >
              {t('ReportSequence:lastMonth')}
            </PredefinedFilter>
            <PredefinedFilter
              onClick={() => {
                const date = new Date();
                date.setFullYear(date.getFullYear() - 1);
                date.setMonth(11);
                date.setDate(31);
                const to = date.toISOString().split('T')[0];
                date.setMonth(0);
                date.setDate(1);
                const from = date.toISOString().split('T')[0];
                pushDateFilterEvent('last_year');
                setAddedOnAndLoad(from, to);
              }}
              data-icom={'seq-filter-lastYear'}
            >
              {t('ReportSequence:lastYear')}
            </PredefinedFilter>
            <PredefinedFilter
              onClick={() => {
                pushDateFilterEvent('whole_period');
                setAddedOnAndLoad(null, null);
              }}
              data-icom={'seq-filter-wholePeriod'}
            >
              {t('ReportSequence:wholePeriod')}
            </PredefinedFilter>
          </PredefinedFiltersWrapper>
          <FiltersContainer>
            <StyledDateInput
              fromYear={sequenceStart}
              toYear={sequenceEnd}
              value={addedOnFrom}
              onDateChange={onAddedOnFromChange}
              dataCy="report-date-filter"
              baseTranslationPath="Report"
              placeholder={t('dateFilterPlaceholder')}
              label={t(typeCode === TopicGroupTypeCode.Onboarding ? 'labelAddedOnFromOnb' : 'labelAddedOnFrom')}
            />
            <StyledDateInput
              fromYear={sequenceStart}
              toYear={sequenceEnd}
              value={addedOnTo}
              onDateChange={onAddedOnToChange}
              dataCy="report-date-filter"
              baseTranslationPath="Report"
              placeholder={t('dateFilterPlaceholder')}
              label={t(typeCode === TopicGroupTypeCode.Onboarding ? 'labelAddedOnToOnb' : 'labelAddedOnTo')}
            />

            <SequenceTeamFilterContainer>
              <GlobalTeamFilter
                rootTeamsIds={rootTeamsIds}
                setFilters={(filters) => {
                  setTeamsFilter((filters[0]?.values as string[]) ?? []);
                }}
                respondentId={respondentId}
                filters={
                  teamsFilter.length > 0
                    ? [
                        {
                          operator: 'unit-teams',
                          values: teamsFilter,
                        },
                      ]
                    : []
                }
                isSequenceFilter={true}
                aggregatedData={aggregatedData}
                organizationId={organizationId}
              />
            </SequenceTeamFilterContainer>

            {typeCode === TopicGroupTypeCode.Hiring && (
              <>
                <SequenceTeamFilterContainer data-icom={'report-position-filter'}>
                  <GlobalGenericFilter
                    options={
                      hiringInfo?.positions.map((item) => ({
                        value: item.id,
                        label: item.label,
                        count: intersection(
                          ...[item.candidateIds, recruiterCandidateIds, managerCandidateIds].filter(Boolean),
                        ).length,
                      })) ?? []
                    }
                    disabled={!hiringInfo?.positions.length}
                    setFilters={setFilters}
                    filters={filters}
                    filterWithCount={true}
                    type="position"
                    className="same-size figure"
                    organizationId={organizationId}
                  />
                </SequenceTeamFilterContainer>
                <SequenceTeamFilterContainer data-icom={'report-recruiter-filter'}>
                  <GlobalGenericFilter
                    options={
                      hiringInfo?.recruiters.map((item) => ({
                        value: item.id,
                        label: item.label,
                        count: intersection(
                          ...[item.candidateIds, positionCandidateIds, managerCandidateIds].filter(Boolean),
                        ).length,
                      })) ?? []
                    }
                    disabled={!hiringInfo?.recruiters.length}
                    setFilters={setFilters}
                    filters={filters}
                    filterWithCount={true}
                    type="recruiter"
                    className="same-size figure"
                    organizationId={organizationId}
                  />
                </SequenceTeamFilterContainer>
                <SequenceTeamFilterContainer data-icom={'report-manager-filter'}>
                  <GlobalGenericFilter
                    options={
                      hiringInfo?.managers.map((item) => ({
                        value: item.id,
                        label: item.label,
                        count: intersection(
                          ...[item.candidateIds, recruiterCandidateIds, positionCandidateIds].filter(Boolean),
                        ).length,
                      })) ?? []
                    }
                    disabled={!hiringInfo?.managers.length}
                    setFilters={setFilters}
                    filters={filters}
                    filterWithCount={true}
                    type="manager"
                    className="same-size figure"
                    organizationId={organizationId}
                  />
                </SequenceTeamFilterContainer>
              </>
            )}
          </FiltersContainer>
        </FiltersScrollContainer>
        <InfoContainer>
          <ResultsInfo>
            {loading ? (
              <CustomLoadingLine />
            ) : (
              t('displayedRespondents', {
                filtered: filteredRespondentsCount,
                all: allRespondentsCount ? allRespondentsCount : '-',
              })
            )}
          </ResultsInfo>
          <Button
            variant="outline-primary"
            data-cy={`seq-filter-refresh`}
            data-icom={'seq-filter-refresh'}
            onClick={() => {
              if (addedOnFrom || addedOnTo) {
                pushDateFilterEvent('custom_date_range', `${addedOnFrom ?? ''}:${addedOnTo ?? ''}`);
              }
              onConfirm();
            }}
          >
            {t('ReportSequence:refresh')}
          </Button>
        </InfoContainer>
      </FilterWrapper>
    );
  },
);

export default GlobalSequenceFilter;
